import { useCallback, useRef, useState } from 'react';
import type { DragEvent, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AddIcon, DeleteIcon, RearrangeIcon } from '../../../bombiix-icon';
import { NewInput } from '../../PIM/common/NewInput';
import type { OptionReorderHandlers, OptionRowProps } from './option-row.types';

function OptionRow({
  index,
  label,
  placeholder,
  inputRef,
  onLabelChange,
  isLast,
  canDelete,
  onAdd,
  onDelete,
  defaultControl,
  isDragOver,
  onReorderStart,
  onReorderOver,
  onReorderDrop,
  onReorderEnd,
  inputStartAdornment,
  inputClassName,
  inputStyle,
}: OptionRowProps) {
  const { t } = useTranslation('custom-module-system-canvas');

  const handleDragStart = (event: DragEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    event.dataTransfer.setData('text/plain', String(index));
    onReorderStart(index);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    onReorderOver(index);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onReorderDrop(index);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (isLast && label.trim()) onAdd();
  };

  return (
    <div className={['cm-option-row', isDragOver ? 'cm-option-row--over' : ''].filter(Boolean).join(' ')} onDragOver={handleDragOver} onDrop={handleDrop}>
      <span className="cm-option-row__handle" draggable onDragStart={handleDragStart} onDragEnd={onReorderEnd} aria-hidden="true">
        <RearrangeIcon size={OPTION_ICON_SIZE} />
      </span>
      <div className={['cm-option-row__input', inputClassName].filter(Boolean).join(' ')} style={inputStyle}>
        <NewInput
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={label}
          onChange={(event) => onLabelChange(event.target.value)}
          onKeyDown={handleKeyDown}
          {...(inputStartAdornment ? { startAdornment: inputStartAdornment } : {})}
        />
      </div>
      {isLast && (
        <button type="button" className="cm-option-row__icon" aria-label={t('COMMON.ADD')} onClick={onAdd}>
          <AddIcon size={OPTION_ICON_SIZE} />
        </button>
      )}
      {canDelete && (
        <button type="button" className="cm-option-row__icon" aria-label={t('COMMON.DELETE')} onClick={onDelete}>
          <DeleteIcon size={OPTION_ICON_SIZE} />
        </button>
      )}
      {defaultControl}
    </div>
  );
}

export function useOptionReorder(onMove: (fromIndex: number, toIndex: number) => void): OptionReorderHandlers {
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const onReorderStart = useCallback((index: number) => {
    dragIndexRef.current = index;
  }, []);

  const onReorderOver = useCallback((index: number) => {
    if (dragIndexRef.current !== null) {
      setDragOverIndex(index);
    }
  }, []);

  const onReorderEnd = useCallback(() => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  }, []);

  const onReorderDrop = useCallback(
    (index: number) => {
      const fromIndex = dragIndexRef.current;
      if (fromIndex !== null && fromIndex !== index) {
        onMove(fromIndex, index);
      }
      onReorderEnd();
    },
    [onMove, onReorderEnd],
  );

  return { dragOverIndex, onReorderStart, onReorderOver, onReorderDrop, onReorderEnd };
}

export function moveOption<T>(options: T[], fromIndex: number, toIndex: number): T[] {
  const next = [...options];
  const [moved] = next.splice(fromIndex, 1);
  if (moved !== undefined) {
    next.splice(toIndex, 0, moved);
  }
  return next;
}

export default OptionRow;

const OPTION_ICON_SIZE = 20;
